import { useTranslation } from 'react-i18next';

import { intensityRangeText, intensityText } from '../../../utils/intensityScale';
import { isAbsent, PROFILE_SCALE_MAX, ProfileRange } from '../profile';

interface ProfileRangeBarProps {
  label: string;
  range: ProfileRange;
}

/**
 * One trait of the style profile as a range on the 0-5 scale: two dots joined by a bar,
 * a single dot when the range is one level wide, and "None" when the trait is absent.
 */
export const ProfileRangeBar = ({ label, range }: ProfileRangeBarProps) => {
  const [t, i18n] = useTranslation();
  const language = i18n?.language;

  const rangeText = intensityRangeText(t, range.min, range.max, language);

  const absent = isAbsent(range);
  const single = range.min === range.max;
  const from = (range.min / PROFILE_SCALE_MAX) * 100;
  const to = (range.max / PROFILE_SCALE_MAX) * 100;

  return (
    <div className="space-y-1 font-sans" data-testid="profile-range">
      <div className="flex justify-between gap-2 text-xs font-semibold text-primary-700 dark:text-primary-300">
        <span>{label}</span>
        <span className="font-bold">
          {rangeText}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${label}: ${rangeText}`}
        className="relative h-2 w-full rounded-full bg-primary-100 dark:bg-primary-900/40"
      >
        {!absent && (
          <>
            {!single && (
              <div
                className="absolute top-0 h-full rounded-full bg-amber-500 dark:bg-amber-400"
                style={{ left: `${from}%`, width: `${to - from}%` }}
              />
            )}
            {[from, ...(single ? [] : [to])].map((position) => (
              <div
                key={position}
                data-testid="profile-dot"
                className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-solid border-amber-500 bg-white shadow dark:bg-slate-800"
                style={{ left: `calc(${position}% - 7px)` }}
              />
            ))}
          </>
        )}
      </div>
      <div className="flex justify-between text-[9px] font-bold uppercase tracking-wider text-primary-400 dark:text-primary-500">
        <span>{intensityText(t, 0, language)}</span>
        <span>{intensityText(t, 3, language)}</span>
        <span>{intensityText(t, PROFILE_SCALE_MAX, language)}</span>
      </div>
    </div>
  );
};
